/**
 * Main Taskpane Entry Point
 * Orchestrates environment detection and authentication flows
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { ERROR_MESSAGES } from "../constants/constants";
import { detectEnvironment, applyDevelopmentOverride } from "../utils/environmentDetection";
import { handleAuthentication, getComponentForAuthResult } from "../auth/authController";
import { initializePendoFromStorage } from "../utils/pendoTracking";
import { getPolicyGuidFromDocumentProperties, isWopiEnvironment } from "../services/legalReferenceService";

// Lazy load components
const PolicyReference = React.lazy(() => import("../components/PolicyReference/PolicyReference"));
const Login = React.lazy(() => import("../components/Login/Login"));
const ServiceUnavailable = React.lazy(() => import("../components/ServiceUnavailable/ServiceUnavailable"));
const BrowserNotSupported = React.lazy(() => import("../components/ServiceUnavailable/BrowserNotSupported"));
const AuthenticationError = React.lazy(() => import("../components/AuthenticationError/AuthenticationError"));

const LoadingSpinner = () => (
  <div style={{ display: "flex", justifyContent: "center", marginTop: "50px" }}>
    <Spinner label="Loading..." size={SpinnerSize.large} />
  </div>
);

// Global flag to track Office readiness
window.officeReady = false;

/**
 * Read addonkey token from document custom properties
 */
const readTokenFromDocument = async () => {
  try {
    return await Word.run(async (context) => {
      const doc = context.document;
    const ooxml = doc.body.getOoxml(); // This may not include the custom properties part
    await context.sync();


    // Try searching the XML for addonkey (may or may not work depending on how Word loads it)
    if (ooxml.value.includes("addonkey")) {
    }
      const customProps = context.document.customDocumentProperties;
      
      let addonKeyProp;
      try {
        addonKeyProp = customProps.getItem("addonkey");
      } catch (getItemError) {
        return null;
      }
      
      context.load(addonKeyProp, "value");
      await context.sync();
      
      const tokenValue = addonKeyProp.value;
      
      return tokenValue || null;
    });
  } catch (error) {
    console.error("[DOC-TOKEN] ❌ Error reading token:", error.message);
    return null;
  }
};

/**
 * Initialize token from document custom properties
 */
const initializeDocumentToken = async () => {
  try {
    const documentToken = await readTokenFromDocument();
    
    if (documentToken) {
      localStorage.setItem("document_addonkey", documentToken);
      return documentToken;
    } else {
      return null;
    }
  } catch (error) {
    console.error("[DOC-TOKEN-INIT] ❌ Error:", error.message);
    return null;
  }
};


/**
 * Get current user session ID from document custom properties (Chetan's debugging code)
 */
async function getCurrentUserSessionId() {
  try {
    return await Word.run(async (context) => {
      const customDocProps = context.document.properties.customProperties;
      context.load(customDocProps, 'items');
      await context.sync();
      const sessionIdProp = customDocProps.items.find(p => p.key === 'CurrentUserSessionId');
      return sessionIdProp ? sessionIdProp.value : null;
    });
  } catch (error) {
    console.error('[CHETAN-DEBUG] Error reading custom property:', error);
    return null;
  }
}

/**
 * Initialize with session ID (Chetan's debugging code)
 */
function initializeWithSessionId(sessionId) {
  // Add any additional initialization logic here if needed
}

/**
 * Main application initialization
 */
Office.onReady(async (info) => {
  
  // Set Office ready flag
  window.officeReady = true;
  
  // Chetan's debugging code - check for session ID
  if (info.host === Office.HostType.Word) {
    try {
      const sessionId = await getCurrentUserSessionId();
      if (sessionId) {
        initializeWithSessionId(sessionId);
      } else {
      }
    } catch (sessionError) {
      console.error('[CHETAN-DEBUG] Error checking session ID:', sessionError);
    }
  }
  
  // Initialize React root
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    console.error("[TASKPANE] ❌ Root element not found!");
    console.error(ERROR_MESSAGES.ROOT_NOT_FOUND);
    return;
  }

  const root = ReactDOM.createRoot(rootElement);

   // Initialize document token reading (NEW FEATURE)
   try {
    await initializeDocumentToken();
  } catch (tokenError) {
    console.error("[TASKPANE] ❌ Document token failed:", tokenError.message);
  }

  // Eagerly read PolicyGuid from document custom properties (injected by backend in WOPI GetFile)
  // This warms up the in-memory cache so subsequent getCurrentPolicyGuid() calls are fast
  try {
    if (isWopiEnvironment()) {
      const docPolicyGuid = await getPolicyGuidFromDocumentProperties();
      if (docPolicyGuid) {
        console.log("[TASKPANE] ✅ PolicyGuid from document properties:", docPolicyGuid);
      } else {
        console.log("[TASKPANE] ⚠️ No PolicyGuid in document properties (will fall back to dialog cookie)");
      }
    }
  } catch (policyGuidError) {
    console.error("[TASKPANE] ❌ PolicyGuid document property read failed:", policyGuidError.message);
  }

  try {
    // Step 1: Detect environment
    const detectedEnvironment = detectEnvironment(info);
    
    // Step 2: Apply development overrides if needed
    const DEV_FORCE_MODE = 'auto'; // Change for testing: 'wopi' | 'sharepoint' | 'desktop' | 'auto'
    const environment = applyDevelopmentOverride(DEV_FORCE_MODE, detectedEnvironment);
    
    
    // Step 3: Handle authentication based on environment
    const authResult = await handleAuthentication(environment);
    
    // Step 4: Render appropriate component
    const componentToRender = getComponentForAuthResult(authResult);
    await renderComponent(root, componentToRender, authResult);
    
  } catch (error) {
    console.error("[TASKPANE] ❌ Initialization error:", error.message);
    console.error(ERROR_MESSAGES.SESSION_VALIDATION_FAILED, error);
    
    root.render(
      <React.Suspense fallback={<LoadingSpinner />}>
        <ServiceUnavailable />
      </React.Suspense>
    );
  }
});

/**
 * Render the appropriate component based on authentication result
 */
const renderComponent = async (root, componentName, authResult) => {
  
  switch (componentName) {
    case 'PolicyReference':
      // Initialize Pendo analytics for authenticated user
      try {
        initializePendoFromStorage();
        console.log("[TASKPANE] 📊 Pendo initialization attempted for authenticated user");
      } catch (pendoError) {
        console.warn("[TASKPANE] ⚠️ Pendo initialization failed:", pendoError.message);
      }
      
      root.render(
        <React.Suspense fallback={<LoadingSpinner />}>
          <PolicyReference />
        </React.Suspense>
      );
      break;
      
    case 'Login':
      root.render(
        <React.Suspense fallback={<LoadingSpinner />}>
          <Login authType={authResult.authType} />
        </React.Suspense>
      );
      break;
      
    case 'AuthenticationError':
      root.render(
        <React.Suspense fallback={<LoadingSpinner />}>
          <AuthenticationError statusCode={authResult.statusCode} />
        </React.Suspense>
      );
      break;
    case 'BrowserNotSupported':
      root.render(
        <React.Suspense fallback={<LoadingSpinner />}>
          <BrowserNotSupported />
        </React.Suspense>
      );
      break;
      
    case 'ServiceUnavailable':
    default:
      root.render(
        <React.Suspense fallback={<LoadingSpinner />}>
          <ServiceUnavailable />
        </React.Suspense>
      );
      break;
  }
};
